import { ArrowDown01, ListChecks, Loader2, RefreshCw, Scroll } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import useGlobalStore from '@/useGlobalStore.ts'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import api from '@/api.ts'
import {
	LibraryVocabulary,
	PracticeSet,
	PracticeSetAnswer,
	PracticeSetItem,
	PracticeSetItemSentence,
	PracticeSetItemVocabulary,
	PracticeSetItemChoice,
} from '@/models.ts'
import { Button } from '@/components/ui/button.tsx'
import { toast } from 'sonner'
import { Sheet } from '@/components/ui/sheet.tsx'
import { ItemsSheet } from '@/components/items-sheet.tsx'
import { useBus } from '@/useBus.ts'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx'
import { cn } from '@/lib/utils.ts'
import { LoadingButton } from '@/components/loading-button.tsx'
import RabbitFlow from '@/assets/rabbitflow.png'

interface PracticeOverviewProps {
	onPracticeLoaded: (
		practiceSet: PracticeSet,
		practiceItems: PracticeSetItem[],
		practiceSetAnswers?: PracticeSetAnswer,
	) => void
}

const PracticeOverview = ({ onPracticeLoaded }: PracticeOverviewProps) => {
	const { t } = useTranslation()
	const [openVocabularySheet, setOpenVocabularySheet] = useState(false)
	const {
		practiceOverview,
		user,
		learningLanguageCode,
		setIsSheetVisible,
		availableAppLanguages,
		appLanguageCode,
		setCurrentPracticeStatus,
	} = useGlobalStore()
	const [latestReadyPracticeSets, setLatestReadyPracticeSets] = useState<{
		[key: string]: {
			loading: boolean
			starting: boolean
			practiceSet: PracticeSet | null
		}
	}>({
		anki: {
			loading: false,
			starting: false,
			practiceSet: null,
		},
		sentences: {
			loading: false,
			starting: false,
			practiceSet: null,
		},
		choices: {
			loading: false,
			starting: false,
			practiceSet: null,
		},
	})
	const [filteredLibraryVocabularyList, setFilteredLibraryVocabularyList] = useState<LibraryVocabulary[] | null>(null)
	const [activeTab, setActiveTab] = useState('anki')
	const [showLeftFade, setShowLeftFade] = useState(false)
	const [showRightFade, setShowRightFade] = useState(false)
	const tabsContainerRef = useRef<HTMLDivElement>(null)
	const [loadingCompletedPracticeSetId, setLoadingCompletedPracticeSetId] = useState<number | null>(null)

	const fetchPracticeSetItems = async (practiceSet: PracticeSet) => {
		if (practiceSet.type === 'anki') {
			setLatestReadyPracticeSets((prevState) => ({
				...prevState,
				[practiceSet.type]: {
					...prevState[practiceSet.type],
					starting: true,
				},
			}))
			const practiceSetItems: PracticeSetItemVocabulary[] = await api(
				`/v2/practice/sets/items/${practiceSet.type}/${practiceSet.id}`,
			)
				.finally(() =>
					setTimeout(
						() =>
							setLatestReadyPracticeSets((prevState) => ({
								...prevState,
								[practiceSet.type]: {
									...prevState[practiceSet.type],
									starting: false,
								},
							})),
						500,
					),
				)
				.catch((error) => {
					toast(error.data?.message || 'Unknown error')
				})
			if (practiceSetItems.length > 0) {
				onPracticeLoaded(practiceSet, practiceSetItems)
			} else {
				toast(t('Nothing to practice.'))
			}
		} else if (practiceSet.type === 'sentences') {
			setLatestReadyPracticeSets((prevState) => ({
				...prevState,
				[practiceSet.type]: {
					...prevState[practiceSet.type],
					starting: true,
				},
			}))
			const practiceSetItems: PracticeSetItemSentence[] = await api(
				`/v2/practice/sets/items/${practiceSet.type}/${practiceSet.id}`,
			)
				.finally(() =>
					setTimeout(
						() =>
							setLatestReadyPracticeSets((prevState) => ({
								...prevState,
								[practiceSet.type]: {
									...prevState[practiceSet.type],
									starting: false,
								},
							})),
						500,
					),
				)
				.catch((error) => {
					toast(error.data?.message || 'Unknown error')
				})
			if (practiceSetItems.length > 0) {
				onPracticeLoaded(practiceSet, practiceSetItems)
			} else {
				toast(t('Nothing to practice.'))
			}
		} else if (practiceSet.type === 'choices') {
			setLatestReadyPracticeSets((prevState) => ({
				...prevState,
				[practiceSet.type]: {
					...prevState[practiceSet.type],
					starting: true,
				},
			}))
			const practiceSetItems: PracticeSetItemChoice[] = await api(
				`/v2/practice/sets/items/${practiceSet.type}/${practiceSet.id}`,
			)
				.finally(() =>
					setTimeout(
						() =>
							setLatestReadyPracticeSets((prevState) => ({
								...prevState,
								[practiceSet.type]: {
									...prevState[practiceSet.type],
									starting: false,
								},
							})),
						500,
					),
				)
				.catch((error) => {
					toast(error.data?.message || 'Unknown error')
				})
			if (practiceSetItems.length > 0) {
				onPracticeLoaded(practiceSet, practiceSetItems)
			} else {
				toast(t('Nothing to practice.'))
			}
		}
	}

	const fetchLatestReadyPracticeSet = async (refresh = false) => {
		setLatestReadyPracticeSets((prevState) => ({
			...prevState,
			anki: {
				...prevState['anki'],
				loading: true,
			},
			sentences: {
				...prevState['sentences'],
				loading: true,
			},
			choices: {
				...prevState['choices'],
				loading: true,
			},
		}))
		const practiceSets: { [key: string]: PracticeSet } = await api(
			`/v3/practice/sets/latest/${learningLanguageCode}?status=ready&app_language_code=${appLanguageCode}${refresh ? '&refresh=1' : ''}`,
		).finally(() => {
			setLatestReadyPracticeSets((prevState) => ({
				...prevState,
				anki: {
					...prevState['anki'],
					loading: false,
				},
				sentences: {
					...prevState['sentences'],
					loading: false,
				},
				choices: {
					...prevState['choices'],
					loading: false,
				},
			}))
		})
		if (practiceSets !== undefined) {
			setLatestReadyPracticeSets((prevState) => ({
				...prevState,
				anki: {
					...prevState['anki'],
					practiceSet: practiceSets['anki'],
				},
				sentences: {
					...prevState['sentences'],
					practiceSet: practiceSets['sentences'],
				},
				choices: {
					...prevState['choices'],
					practiceSet: practiceSets['choices'],
				},
			}))
		}
	}

	const onVocabularyLearningStatusClicked = async (status: string) => {
		setOpenVocabularySheet(true)
		setFilteredLibraryVocabularyList(null)
		const libraryVocabularies: LibraryVocabulary[] = await api(
			`/v1/library/vocabulary/list/${learningLanguageCode}?learning_stage=${status}`,
			{
				method: 'GET',
			},
		)
		setFilteredLibraryVocabularyList(libraryVocabularies)
	}

	const checkScroll = () => {
		const container = tabsContainerRef.current
		if (container) {
			const isRTL = availableAppLanguages[appLanguageCode]?.dir === 'rtl'
			const maxScrollWidth = container.scrollWidth - container.clientWidth
			const needsScrolling = container.scrollWidth > container.clientWidth

			if (isRTL) {
				// In RTL, scrollLeft is negative when scrolled to the right
				// and becomes more negative as you scroll further right
				// For RTL: We're at left edge when scrollLeft is 0 or positive
				const isAtLeftEdge = container.scrollLeft >= 0
				// For RTL: We're at right edge when abs(scrollLeft) >= maxScrollWidth (with buffer)
				const isAtRightEdge = Math.abs(container.scrollLeft) >= maxScrollWidth - 1
				// Only show left fade if not at left edge
				setShowLeftFade(needsScrolling && !isAtLeftEdge)
				// Only show right fade if not at right edge
				setShowRightFade(needsScrolling && !isAtRightEdge)
			} else {
				// Original LTR behavior
				// Show left fade only if scrolled to the right
				setShowLeftFade(container.scrollLeft > 0)
				// Only show right fade if not at the end
				const isAtEnd = container.scrollLeft >= maxScrollWidth - 1
				setShowRightFade(needsScrolling && !isAtEnd)
			}
		}
	}

	const onStartPractice = async (type: string) => {
		if (type === 'anki' || type === 'sentences' || type === 'choices') {
			if (latestReadyPracticeSets[type]?.practiceSet !== null) {
				await fetchPracticeSetItems(latestReadyPracticeSets[type]?.practiceSet)
			}
		} else {
			toast(t('Not supported'))
		}
	}

	const openCompletedPracticeSet = async (practiceSet: PracticeSet) => {
		setLoadingCompletedPracticeSetId(practiceSet.id)
		const answerResult: PracticeSetAnswer = await api(`/v2/practice/sets/${practiceSet.id}`)
			.catch((error) => {
				toast(error.data?.message || 'Unknown error')
			})
			.finally(() => setLoadingCompletedPracticeSetId(null))
		onPracticeLoaded(practiceSet, [], answerResult)
		setCurrentPracticeStatus('finished')
	}

	useEffect(() => {
		if (user !== null) {
			fetchLatestReadyPracticeSet().then()
		}
	}, [user])

	useBus('close-sheet', () => {
		setOpenVocabularySheet(false)
	})

	useEffect(() => {
		setIsSheetVisible(openVocabularySheet)
	}, [openVocabularySheet])

	useEffect(() => {
		const container = tabsContainerRef.current
		if (container) {
			// Important: Use a timeout to ensure the DOM has fully updated
			// This addresses potential timing issues where scrollWidth might not be correct immediately
			const timer = setTimeout(() => {
				checkScroll()
			}, 0)

			// Add event listeners
			container.addEventListener('scroll', checkScroll)
			window.addEventListener('resize', checkScroll)

			// Cleanup
			return () => {
				clearTimeout(timer)
				container.removeEventListener('scroll', checkScroll)
				window.removeEventListener('resize', checkScroll)
			}
		}
	}, [activeTab, latestReadyPracticeSets, appLanguageCode])

	return (
		<Sheet open={openVocabularySheet} onOpenChange={setOpenVocabularySheet}>
			<div className="flex justify-evenly px-5 mt-2">
				<div
					onClick={() => onVocabularyLearningStatusClicked('learned')}
					className="flex flex-1 flex-col cursor-pointer justify-center items-center rounded-s-md overflow-hidden active:bg-accent bg-input">
					<div className="self-start w-10 h-2 bg-[limegreen]"></div>
					<div className="mt-2 mb-1 text-sm font-semibold text-center">{t('Learned')}</div>
					<div className="mb-2">{practiceOverview?.vocabulary?.stats.learned_count}</div>
				</div>
				<div
					onClick={() => onVocabularyLearningStatusClicked('known')}
					className="flex flex-1 flex-col cursor-pointer justify-center items-center active:bg-accent bg-input">
					<div className="self-start w-10 h-2 bg-[orange]"></div>
					<div className="mt-2 mb-1 text-sm font-semibold text-center">{t('Known')}</div>
					<div className="mb-2">{practiceOverview?.vocabulary?.stats.known_count}</div>
				</div>
				<div
					onClick={() => onVocabularyLearningStatusClicked('needs_work')}
					className="flex flex-1 flex-col cursor-pointer justify-center items-center rounded-e-md overflow-hidden active:bg-accent bg-input">
					<div className="self-start w-10 h-2 bg-[red]"></div>
					<div className="mt-2 mb-1 text-sm font-semibold text-center">{t('Needs Work')}</div>
					<div className="mb-2">{practiceOverview?.vocabulary?.stats.needs_work_count}</div>
				</div>
			</div>
			<div className="flex flex-col items-center basis-1/6 bg-muted pt-2">
				<div className="w-full flex flex-row px-5 py-5">
					<img
						className={`w-24 object-contain ${availableAppLanguages[appLanguageCode]?.dir === 'rtl' ? 'scale-x-[-1]' : ''}`}
						src={RabbitFlow}
					/>
					<div className="flex-1 my-4 ms-6 select-none">
						<div className="font-semibold">{t('Learn, Practice, Master.')}</div>
						<div className="text-sm mt-3">
							{t(
								'Choose a practice category below and start practicing. Practice sets are based on your own library content and they are updated regularly.',
							)}
						</div>
					</div>
				</div>
				<Tabs
					dir={availableAppLanguages[appLanguageCode]?.dir as 'ltr' | 'rtl'}
					defaultValue="vocabulary"
					value={activeTab}
					onValueChange={setActiveTab}
					className="w-full text-center pb-2">
					<div className="relative w-full mx-0">
						<div
							className={cn(
								'absolute start-0 top-0 bottom-0 w-12 from-white dark:from-background to-transparent z-10 pointer-events-none transition-opacity duration-300 ease-in-out',
								showLeftFade ? 'opacity-100' : 'opacity-0',
								availableAppLanguages[appLanguageCode]?.dir === 'ltr'
									? 'bg-gradient-to-r'
									: 'bg-gradient-to-l',
							)}
						/>
						<div
							ref={tabsContainerRef}
							className="w-full overflow-x-auto ps-4"
							style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
							onScroll={checkScroll}>
							<TabsList className="inline-flex min-w-max me-4">
								{Object.keys(latestReadyPracticeSets).map((key) => (
									<TabsTrigger key={key} value={key}>
										{t(key)}
									</TabsTrigger>
								))}
							</TabsList>
						</div>
						<div
							className={cn(
								'absolute end-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white dark:from-background to-transparent z-10 pointer-events-none transition-opacity duration-300 ease-in-out',
								showRightFade ? 'opacity-100' : 'opacity-0',
								availableAppLanguages[appLanguageCode]?.dir === 'ltr'
									? 'bg-gradient-to-l'
									: 'bg-gradient-to-r',
							)}
						/>
					</div>
				</Tabs>
				<div className="flex w-full gap-4 px-5 py-4">
					{latestReadyPracticeSets[activeTab]?.loading ? (
						<Loader2 className="w-9 h-9 animate-spin self-center m-auto" />
					) : (
						<>
							<div className="w-1/3 flex flex-col justify-center rounded-lg">
								<Button
									onClick={() => fetchLatestReadyPracticeSet(true)}
									variant="ghost"
									className="gap-1.5 p-0 active:bg-primary/10"
									size="xs">
									<RefreshCw className="w-4 h-4" />
									<span>{t('Refresh')}</span>
								</Button>
								{/*<Button*/}
								{/*	onClick={() => console.log('options')}*/}
								{/*	variant="ghost"*/}
								{/*	className="gap-1.5 p-0 active:bg-primary/10"*/}
								{/*	size="xs">*/}
								{/*	<Settings className="w-4 h-4" />*/}
								{/*	<span>{t('Options')}</span>*/}
								{/*</Button>*/}
							</div>
							<div className="w-2/3 flex justify-center items-center rounded-lg">
								{latestReadyPracticeSets[activeTab]?.practiceSet !== null ? (
									<LoadingButton
										loading={latestReadyPracticeSets[activeTab]?.starting}
										className="bg-appgreen text-white active:bg-appgreen/40"
										onClick={() => onStartPractice(activeTab)}>
										{t('Start Practice')}
									</LoadingButton>
								) : (
									<div>{t('Unavailable')}</div>
								)}
							</div>
						</>
					)}
				</div>
			</div>
			<div className="flex flex-col w-100 flex-grow-[1] basis-1/5">
				<div className="flex flex-col items-center mb-2">
					<div className="text-sm font-semibold select-none">{t('Recent completed practices')}</div>
				</div>
				<div className="flex flex-row my-1 px-4 select-none">
					<div className="flex w-14 text-sm">
						<ArrowDown01 className="w-5 h-5" />
					</div>
					<div className="flex-[2] text-sm">
						<span className="font-bold text-appgreen">{t('Correct')}</span>&nbsp;/ {t('All count')}
					</div>
					<div className="flex-[2] text-sm ms-2">{t('Practiced at')}</div>
				</div>
				{practiceOverview?.vocabulary.recent_practice_sets !== undefined &&
					practiceOverview?.vocabulary.recent_practice_sets.length <= 0 && (
						<div className="flex justify-center items-center font-bold text-sm mt-4">
							<Scroll className="w-4 h-4 me-2" />
							{t('Not enough data')}
						</div>
					)}
				{practiceOverview?.vocabulary.recent_practice_sets.map((x, idx) => (
					<div
						onClick={() => openCompletedPracticeSet(x)}
						key={idx}
						className="flex flex-row cursor-pointer py-2 px-4 border-white dark:border-black active:bg-accent select-none"
						style={{
							borderBottom:
								idx !== practiceOverview?.vocabulary.recent_practice_sets.length - 1
									? `1px solid`
									: 'none',
						}}>
						<div className="flex items-center w-14">
							{x.id === loadingCompletedPracticeSetId ? (
								<Loader2 className="h-4 w-4 animate-spin" />
							) : (
								x.user_limited_id
							)}
						</div>
						<div className="flex-[2] flex items-center">
							<ListChecks className="h-5 w-5 me-2" />
							<span className="font-bold text-appgreen">{x.correct_answer_count}</span>&nbsp;/{' '}
							{x.items_count}
						</div>
						<div className="flex-[2] ms-2">{dayjs().to(dayjs.utc(x.practiced_at).local())}</div>
					</div>
				))}
			</div>
			<ItemsSheet onClose={() => setOpenVocabularySheet(false)} vocabularyList={filteredLibraryVocabularyList} />
		</Sheet>
	)
}

export default PracticeOverview
